import React, { FC, memo, useEffect, useRef, useState } from 'react';
import classes from './VideoPlayer.module.scss';
import { queueScreenPlayBtnIcon } from '../../../../assets/icons';
import classNames from 'classnames';
import {
  isQueueScreenVideoAutoplayEnabled,
  isQueueScreenVideoSoundEnabled
} from '../../../../constants/API';
import { isAndroidTabloDevice } from '../../../../utils/QueuePage/tabloDevice';

type VideoPlayerProps = {
  videoPlaylist?: string[] | null;
  /** При объявлении: на TV лучше временно mute, а не pause (pause рвёт HW-декодер). */
  isPauseVideo: boolean;
  volume: number;
  noRoundedCorners?: boolean;
};

const isAndroidTablo = isAndroidTabloDevice();

const VideoPlayer: FC<VideoPlayerProps> = ({
  videoPlaylist,
  isPauseVideo,
  volume,
  noRoundedCorners
}) => {
  const playlist = videoPlaylist ?? [];
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showPlayButton, setShowPlayButton] = useState(!isQueueScreenVideoAutoplayEnabled);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const wantContentSound = isQueueScreenVideoSoundEnabled;
  // На Android при объявлении только mute, без pause
  const duckOnly = isAndroidTablo;
  const normalizedVolume = Number.isFinite(volume)
    ? Math.min(1, Math.max(0, Math.round(volume * 100) / 100))
    : 0.2;

  const isSingleClip = playlist.length <= 1;
  const safeIndex = playlist.length > 0 ? Math.min(currentVideoIndex, playlist.length - 1) : 0;
  const currentUrl = playlist[safeIndex] ?? '';

  useEffect(() => {
    if (playlist.length > 0 && currentVideoIndex >= playlist.length) {
      setCurrentVideoIndex(0);
    }
  }, [playlist.length, currentVideoIndex]);

  // Смена URL — пробуем play со звуком (Fully Kiosk обычно разрешает)
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !currentUrl) return;

    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
    if ('disableRemotePlayback' in video) {
      (video as HTMLVideoElement & { disableRemotePlayback: boolean }).disableRemotePlayback =
        true;
    }

    if (!isQueueScreenVideoAutoplayEnabled) return;

    video.muted = !wantContentSound;
    if (wantContentSound) {
      video.volume = normalizedVolume;
    }

    const playPromise = video.play();
    if (playPromise === undefined) return;

    playPromise
      .then(() => setShowPlayButton(false))
      .catch(() => {
        // Autoplay со звуком запрещён — пробуем muted, кнопка Play разблокирует звук
        if (wantContentSound) {
          video.muted = true;
          video
            .play()
            .then(() => setShowPlayButton(true))
            .catch(() => setShowPlayButton(true));
        } else {
          setShowPlayButton(true);
        }
      });
  }, [currentUrl, wantContentSound]);

  // Громкость / duck при объявлении — без remount
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const shouldMute = !wantContentSound || (duckOnly && isPauseVideo);
    video.muted = shouldMute;
    if (!shouldMute) {
      video.volume = normalizedVolume;
    }

    if (!duckOnly && isPauseVideo) {
      video.pause();
      return;
    }

    if (isQueueScreenVideoAutoplayEnabled && video.paused && !showPlayButton) {
      video.play().catch(() => setShowPlayButton(true));
    }
  }, [isPauseVideo, normalizedVolume, wantContentSound, duckOnly, showPlayButton]);

  const replayCurrent = () => {
    const video = videoRef.current;
    if (!video) return;
    try {
      video.currentTime = 0;
    } catch {
      // ignore
    }
    video.muted = !wantContentSound || (duckOnly && isPauseVideo);
    if (wantContentSound && !video.muted) {
      video.volume = normalizedVolume;
    }
    video
      .play()
      .then(() => setShowPlayButton(false))
      .catch(() => setShowPlayButton(true));
  };

  const handleEnded = () => {
    if (playlist.length === 0) return;
    if (isSingleClip) {
      replayCurrent();
      return;
    }
    const nextIndex = safeIndex === playlist.length - 1 ? 0 : safeIndex + 1;
    if (playlist[nextIndex] === currentUrl) {
      setCurrentVideoIndex(nextIndex);
      replayCurrent();
      return;
    }
    setCurrentVideoIndex(nextIndex);
  };

  const handlePlayClick = () => {
    const video = videoRef.current;
    if (!video) return;
    // Жест пользователя — разблокируем звук
    video.muted = !wantContentSound || (duckOnly && isPauseVideo);
    if (wantContentSound && !video.muted) {
      video.volume = normalizedVolume;
    }
    video
      .play()
      .then(() => setShowPlayButton(false))
      .catch(() => setShowPlayButton(true));
  };

  if (!currentUrl) {
    return null;
  }

  return (
    <div
      className={classNames(classes.videoPlayerWrapper, {
        [classes.videoPlayerWrapperNoRounding]: noRoundedCorners,
        [classes.videoPlayerWrapperAndroid]: isAndroidTablo
      })}>
      <video
        key={currentUrl}
        ref={videoRef}
        className={classes.videoElement}
        src={currentUrl}
        autoPlay={isQueueScreenVideoAutoplayEnabled}
        loop={isSingleClip}
        muted={!wantContentSound}
        playsInline
        preload="auto"
        controls={false}
        onEnded={isSingleClip ? undefined : handleEnded}
      />

      {showPlayButton && (
        <button type="button" onClick={handlePlayClick} className={classes.playBtn}>
          <img className={classes.imgIcon} src={queueScreenPlayBtnIcon} alt="PlayBtnIcon" />
        </button>
      )}
    </div>
  );
};

const arePlaylistEqual = (a?: string[] | null, b?: string[] | null) => {
  const left = a ?? [];
  const right = b ?? [];
  if (left === right) return true;
  if (left.length !== right.length) return false;
  return left.every((url, index) => url === right[index]);
};

export default memo(VideoPlayer, (prev, next) => {
  return (
    prev.isPauseVideo === next.isPauseVideo &&
    prev.volume === next.volume &&
    prev.noRoundedCorners === next.noRoundedCorners &&
    arePlaylistEqual(prev.videoPlaylist, next.videoPlaylist)
  );
});
